import React from "react";
import { Box, MenuItem, Typography } from "@mui/material";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import { alpha } from "@mui/material/styles";
import ComponentLoading from "../App/ComponentLoading";

const DownloadMenuOptions = ({ isGenerating, onDownload }) => {
  const menuSections = [
    {
      title: "ZIP Format",
      items: [
        {
          label: "Download as ZIP (Draft)",
          description: "Includes all schedules in draft mode",
          downloadType: "zip",
          forView: true,
          icon: <FolderZipIcon color='primary' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.primary.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
        {
          label: "Download as ZIP (Final)",
          description: "Includes all schedules in final mode",
          downloadType: "zip",
          forView: false,
          icon: <FolderZipIcon color='primary' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.primary.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
      ],
    },
    {
      title: "PDF Format",
      items: [
        {
          label: "Download as PDF (Draft)",
          description: "Single PDF with all schedules in draft mode",
          downloadType: "merged",
          forView: true,
          icon: <PictureAsPdfIcon color='error' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.error.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
        {
          label: "Download as PDF (Final)",
          description: "Single PDF with all schedules in final mode",
          downloadType: "merged",
          forView: false,
          icon: <PictureAsPdfIcon color='error' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.error.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
      ],
    },
    {
      title: "Excel Format",
      items: [
        {
          label: "Download as Excel (Draft)",
          description: "Download all statements and schedules as Excel drafts",
          downloadType: "excel",
          forView: true,
          icon: <TableChartIcon color='success' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.success.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.success.main, 0.08),
        },
        {
          label: "Download as Excel (Final)",
          description: "Download all statements and schedules as final Excel files",
          downloadType: "excel",
          forView: false,
          icon: <TableChartIcon color='success' fontSize='small' />,
          iconBg: (theme) => alpha(theme.palette.success.main, 0.1),
          hoverBg: (theme) => alpha(theme.palette.success.main, 0.08),
        },
      ],
    },
  ];

  const renderMenuItem = (item, index, sectionLength) => (
    <MenuItem
      key={`${item.downloadType}-${item.forView}-${index}`}
      onClick={() => onDownload(item.downloadType, item.forView)}
      disabled={isGenerating}
      sx={{
        borderRadius: 1,
        mx: 1,
        mb: index === sectionLength - 1 ? 1 : 0.5,
        py: 1.5,
        "&:hover": {
          bgcolor: item.hoverBg,
        },
        "&:disabled": {
          opacity: 0.6,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            bgcolor: item.iconBg,
          }}
        >
          {isGenerating ? <ComponentLoading message='' /> : item.icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {item.label}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {item.description}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  );

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      {menuSections.map((section) => (
        <Box key={section.title}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ px: 2, py: 1, display: "block" }}
          >
            {section.title}
          </Typography>
          {section.items.map((item, index) =>
            renderMenuItem(item, index, section.items.length)
          )}
        </Box>
      ))}
    </Box>
  );
};

export default DownloadMenuOptions;
